/**
 * useDryRunFlow — OutpostForge frontend (CES standard)
 * Wraps the dry-run state machine and the capacity-task repository behind
 * one hook: MANDATORY dry run before apply, editing invalidates, apply uses
 * the exact pools that passed the dry run (snapshot). Apply outcomes are
 * also recorded in the notification store (toast UI arrives with the CES
 * re-skin).
 */

import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import type { InstanceMap } from 'slotting-core';
import {
  capacityTaskRepository,
  type CapacityTaskRepository,
} from '../repositories/CapacityTaskRepository';
import { describeApiError } from '../services/apiErrors';
import { dryRunReducer, initialDryRunState } from '../services/dryRunMachine';
import { ApiError } from '../services/http';
import { useNotificationStore } from '../stores/notificationStore';
import type { InstanceTypeCapacity } from '../types/outposts';
import type { ApplyOptions } from '../components/organisms/ConfirmApplyModal';

/**
 * A capacity task dry run is ASYNCHRONOUS: StartCapacityTask returns
 * immediately with status REQUESTED and a task id; the validation result
 * (COMPLETED = within capacity, FAILED = above/below, with a reason) is only
 * available after polling GetCapacityTask until a terminal status. Empirically
 * these complete in ~20s, so we poll with a generous ceiling.
 */
const DRY_RUN_POLL_INTERVAL_MS = 2500;
const DRY_RUN_POLL_TIMEOUT_MS = 120_000;
const DRY_RUN_TERMINAL_STATUSES = new Set(['COMPLETED', 'FAILED']);

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/** Instance map → camelCase pools for the backend API. */
export function toCamelPools(instances: InstanceMap): InstanceTypeCapacity[] {
  return Object.entries(instances)
    .filter(([, count]) => count > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([instanceType, count]) => ({ instanceType, count }));
}

export function useDryRunFlow(
  outpostId: string,
  scope: { assetId?: string },
  repository: CapacityTaskRepository = capacityTaskRepository,
) {
  const [flow, dispatch] = useReducer(dryRunReducer, initialDryRunState);
  /** Snapshot of the pools that passed the dry run — the apply uses these. */
  const [dryRunPools, setDryRunPools] = useState<InstanceTypeCapacity[]>([]);
  const notify = useNotificationStore((state) => state.notify);
  /**
   * Monotonic id of the in-flight dry-run poll. Incremented on every new run
   * and on unmount so stale polls stop dispatching (superseded or gone).
   */
  const runIdRef = useRef(0);

  useEffect(
    () => () => {
      runIdRef.current += 1;
    },
    [],
  );

  const pending = flow.phase === 'dryRunPending' || flow.phase === 'applyPending';

  const notifyEdit = useCallback(() => dispatch({ type: 'EDIT' }), []);

  const runDryRun = useCallback(
    (instances: InstanceMap) => {
      const pools = toCamelPools(instances);
      setDryRunPools(pools);
      dispatch({ type: 'DRY_RUN_START' });
      const runId = ++runIdRef.current;
      const isCurrent = () => runIdRef.current === runId;

      const poll = async (): Promise<void> => {
        try {
          // Kick off the dry run; the response starts as REQUESTED.
          let result = await repository.start(outpostId, {
            ...scope,
            instancePools: pools,
            dryRun: true,
          });

          const deadline = Date.now() + DRY_RUN_POLL_TIMEOUT_MS;
          while (!DRY_RUN_TERMINAL_STATUSES.has(result.status)) {
            if (!isCurrent()) return; // superseded by a newer run or unmounted
            if (!result.capacityTaskId) {
              dispatch({
                type: 'DRY_RUN_ERROR',
                error: 'Dry run did not return a task id to poll',
              });
              return;
            }
            if (Date.now() >= deadline) {
              dispatch({
                type: 'DRY_RUN_ERROR',
                error: `Dry run timed out after ${Math.round(
                  DRY_RUN_POLL_TIMEOUT_MS / 1000,
                )}s (last status: ${result.status})`,
              });
              return;
            }
            await delay(DRY_RUN_POLL_INTERVAL_MS);
            if (!isCurrent()) return;
            result = await repository.get(outpostId, result.capacityTaskId);
          }

          if (isCurrent()) dispatch({ type: 'DRY_RUN_RESULT', result });
        } catch (err: unknown) {
          if (isCurrent()) dispatch({ type: 'DRY_RUN_ERROR', error: describeApiError(err) });
        }
      };

      void poll();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [outpostId, scope.assetId, repository],
  );

  const apply = useCallback(
    (options: ApplyOptions, callbacks?: { onSettled?: () => void }) => {
      dispatch({ type: 'APPLY_START' });
      repository
        .start(outpostId, {
          ...scope,
          instancePools: dryRunPools,
          dryRun: false,
          taskActionOnBlockingInstances: options.taskAction,
          ...(options.instancesToExclude
            ? { instancesToExclude: options.instancesToExclude }
            : {}),
        })
        .then((result) => {
          callbacks?.onSettled?.();
          dispatch({ type: 'APPLY_SUCCESS', result });
          notify('success', `Capacity task ${result.capacityTaskId} started`);
        })
        .catch((err: unknown) => {
          callbacks?.onSettled?.();
          const conflict = err instanceof ApiError && err.status === 409;
          const message = describeApiError(err);
          dispatch({ type: 'APPLY_ERROR', error: message, conflict });
          notify('error', `Apply failed: ${message}`);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [outpostId, scope.assetId, repository, dryRunPools, notify],
  );

  return { flow, pending, notifyEdit, runDryRun, apply };
}
